import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  JOB_COVER_FIELD_LABELS,
  ROOF_CAVITY_NOT_INSPECTED_REASON_LABELS,
  type InspectionJob,
  type JobCover,
  type RoofCavityNotInspectedReason,
  type StoredPhotoManifest,
} from '@cer-autofill/shared';
import {
  apiErrorReason,
  deleteReportPhoto,
  fetchJobCoverContext,
  fetchPhotos,
  publishJobCover,
  resolveCoverConflict,
  saveJobCover,
  uploadQaPhoto,
  type JobCoverContext,
} from '../api.js';
import { ReportPhotoEditor } from './ReportPhotoEditor.js';

const SEED_SOURCE: Record<string, string> = {
  'submission-cover': 'the field report cover',
  'submission-clock': 'the field submission time',
  'partner-cover': 'the related job cover',
  'partner-submission-cover': 'the related job’s report cover',
  'partner-submission-clock': 'the related job’s submission time',
  'photo-exif': 'photo times',
  'partner-photo-exif': 'the related job’s photo times',
  'inspector-profile': 'the inspector profile',
  'owner-email': 'the owner email on the job',
  'partner-owner-email': 'the owner email on the related job',
  'partner-inspector-profile': 'the related job’s inspector profile',
};

/** Cover sheet for a job that has no field report yet. */
export function JobCoverPanel({
  job,
  onSaved,
}: {
  job: InspectionJob;
  onSaved: (job: InspectionJob) => void;
}) {
  const [context, setContext] = useState<JobCoverContext | null>(null);
  const [photos, setPhotos] = useState<StoredPhotoManifest[]>([]);
  const [draft, setDraft] = useState<JobCover>({});
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [next, nextPhotos] = await Promise.all([
      fetchJobCoverContext(job.jobId),
      fetchPhotos(job.jobId),
    ]);
    setContext(next);
    setPhotos(nextPhotos);
    setDraft(next.draft);
  };

  useEffect(() => {
    void reload().catch((e) => setError(apiErrorReason(e)));
    // The job id is the record. A save replaces `job` and this effect runs again.
  }, [job.jobId, job.updatedAt]);

  if (!context) return <p>{error || 'Loading cover sheet…'}</p>;
  const consent = photos.filter((photo) => photo.promptKey === 'consent-signed-form');
  if (context.submittedAt) {
    return (
      <section className="card">
        <h3>Cover sheet</h3>
        <Link
          to={`/reports/${encodeURIComponent(job.jobId)}/${encodeURIComponent(context.submittedAt)}`}
        >
          Open report cover
          {context.unresolvedConflicts.length
            ? ` — ${context.unresolvedConflicts.length} unresolved conflict(s)`
            : ''}
        </Link>
        {context.unresolvedConflicts.length > 0 && (
          <ul>
            {context.unresolvedConflicts.map((conflict) => (
              <li key={conflict.id}>
                {conflict.label}: dashboard {shown(conflict.dashboardValue)}, field{' '}
                {shown(conflict.fieldValue)}. The dashboard value is kept until you choose.
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void resolve(conflict.id, 'dashboard')}
                >
                  Keep dashboard
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void resolve(conflict.id, 'field')}
                >
                  Use field
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
      </section>
    );
  }

  const gaps = coverGaps(context.stored);
  return (
    <section className="card">
      <h3>Cover sheet</h3>
      {context.partnerJobId && (
        <p>
          Related accreditation {context.partnerJobId}. Saving copies blank cover fields there.
          Values already entered on that job stay as they are.
        </p>
      )}
      {!context.consentOnJob && context.consentOnPartner && context.partnerJobId && (
        <p>A consent form is already attached to {context.partnerJobId}. Saving copies it here.</p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {info && <p className="banner banner--success">{info}</p>}
      {editing ? (
        <JobCoverForm
          draft={draft}
          seeds={context.seeds}
          jobId={job.jobId}
          consent={consent}
          busy={busy}
          portal={context.portal}
          onChange={setDraft}
          onUpload={async (promptKey, file) => {
            await uploadQaPhoto(job.jobId, { photoId: crypto.randomUUID(), promptKey, file });
            setPhotos(await fetchPhotos(job.jobId));
          }}
          onDelete={async (photoId) => {
            if (!window.confirm('Delete this consent form? This cannot be undone.')) return;
            await deleteReportPhoto(job.jobId, photoId);
            setPhotos(await fetchPhotos(job.jobId));
          }}
        />
      ) : (
        <JobCoverSummary cover={context.stored} portal={context.portal} seeds={context.seeds} />
      )}
      <div className="action-bar">
        {editing ? (
          <>
            <button type="button" disabled={busy} onClick={() => void save()}>
              Save cover
            </button>
            <button type="button" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                setDraft(context.draft);
                setInfo('');
                setEditing(true);
              }}
            >
              Edit cover sheet
            </button>
            <button
              type="button"
              disabled={busy || gaps.length > 0}
              title={gaps.length > 0 ? `Still needed: ${gaps.join(', ')}` : undefined}
              onClick={() => void publish()}
            >
              Publish for portal autofill
            </button>
          </>
        )}
      </div>
    </section>
  );

  async function save() {
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const saved = await saveJobCover(job, draft);
      onSaved(saved.job);
      setEditing(false);
      setInfo(
        [
          'Cover sheet saved.',
          saved.partnerCopied ? `Blank fields were copied to the related job.` : '',
          saved.consentCopied ? 'The consent form was copied to the related job.' : '',
          saved.partnerConflict
            ? 'The related job changed at the same time, so its cover was not updated. Save again.'
            : '',
        ]
          .filter(Boolean)
          .join(' '),
      );
    } catch (e) {
      setError(apiErrorReason(e));
    } finally {
      setBusy(false);
    }
  }

  async function publish() {
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const result = await publishJobCover(job);
      setInfo(`Portal autofill can fill the cover sheet for ${result.jobIds.join(' and ')}.`);
    } catch (e) {
      setError(apiErrorReason(e));
    } finally {
      setBusy(false);
    }
  }

  async function resolve(id: string, choice: 'dashboard' | 'field') {
    if (!context?.submittedAt) return;
    setBusy(true);
    setError('');
    try {
      await resolveCoverConflict(job.jobId, context.submittedAt, id, choice);
      await reload();
    } catch (e) {
      setError(apiErrorReason(e));
    } finally {
      setBusy(false);
    }
  }
}

export function JobCoverSummary({
  cover,
  portal,
  seeds,
}: {
  cover: JobCover;
  portal: { noConflictOfInterest: boolean; reportReceivedBy: string };
  seeds: readonly { field: string; source: string }[];
}) {
  const rows: Array<[string, string]> = [
    ['Inspection start', stamp(cover.inspectionStartedAt)],
    ['Inspection end', stamp(cover.inspectionEndedAt)],
    ['Inspector ID card number', cover.inspectorIdCardNumber?.trim() || '—'],
    ['No conflict of interest', portal.noConflictOfInterest ? 'Yes' : 'No'],
    [
      'Roof cavity inspected',
      cover.roofCavityInspected === undefined ? '—' : cover.roofCavityInspected ? 'Yes' : 'No',
    ],
    ['Report received by', portal.reportReceivedBy],
    ['Updated owner email', cover.updatedOwnerEmail?.trim() || '—'],
  ];
  return (
    <>
      <dl>
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {seeds.length > 0 && (
        <p>
          Suggested, not saved:{' '}
          {seeds
            .map(
              (seed) =>
                `${JOB_COVER_FIELD_LABELS[seed.field as keyof typeof JOB_COVER_FIELD_LABELS] ?? seed.field} from ${SEED_SOURCE[seed.source] ?? seed.source}`,
            )
            .join('; ')}
          . Edit the cover sheet to keep them.
        </p>
      )}
    </>
  );
}

export function JobCoverForm({
  draft,
  seeds,
  jobId,
  consent,
  busy,
  portal,
  onChange,
  onUpload,
  onDelete,
}: {
  draft: JobCover;
  seeds: readonly { field: string; source: string }[];
  jobId: string;
  consent: readonly StoredPhotoManifest[];
  busy: boolean;
  portal: { noConflictOfInterest: boolean; reportReceivedBy: string };
  onChange: (cover: JobCover) => void;
  onUpload: (promptKey: string, file: File) => Promise<void>;
  onDelete: (photoId: string) => Promise<void>;
}) {
  const set = (patch: Partial<JobCover>) => onChange({ ...draft, ...patch });
  return (
    <div className="form-grid" id="job-cover-editor">
      {seeds.length > 0 && (
        <p>Blank fields were filled from records we already hold. Check them before saving.</p>
      )}
      <label>
        Inspection start
        <input
          type="datetime-local"
          value={forInput(draft.inspectionStartedAt)}
          onChange={(event) => set({ inspectionStartedAt: event.target.value })}
        />
      </label>
      <label>
        Inspection end
        <input
          type="datetime-local"
          value={forInput(draft.inspectionEndedAt)}
          onChange={(event) => set({ inspectionEndedAt: event.target.value })}
        />
      </label>
      <label>
        Inspector ID card number
        <input
          value={draft.inspectorIdCardNumber ?? ''}
          onChange={(event) => set({ inspectorIdCardNumber: event.target.value })}
        />
      </label>
      <label>
        No conflict of interest
        <input readOnly value={portal.noConflictOfInterest ? 'Yes' : 'No'} />
      </label>
      <label>
        Roof cavity inspected
        <select
          value={
            draft.roofCavityInspected === undefined
              ? ''
              : draft.roofCavityInspected
                ? 'true'
                : 'false'
          }
          onChange={(event) => {
            const roofCavityInspected =
              event.target.value === '' ? undefined : event.target.value === 'true';
            set({
              roofCavityInspected,
              ...(roofCavityInspected !== false
                ? { roofCavityNotInspectedReasons: [], roofCavityNotInspectedNote: '' }
                : {}),
            });
          }}
        >
          <option value="">Not recorded</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </label>
      {draft.roofCavityInspected === false &&
        Object.entries(ROOF_CAVITY_NOT_INSPECTED_REASON_LABELS).map(([key, label]) => (
          <label key={key}>
            <input
              type="checkbox"
              checked={
                draft.roofCavityNotInspectedReasons?.includes(
                  key as RoofCavityNotInspectedReason,
                ) ?? false
              }
              onChange={(event) => {
                const reason = key as RoofCavityNotInspectedReason;
                const current = draft.roofCavityNotInspectedReasons ?? [];
                set({
                  roofCavityNotInspectedReasons: event.target.checked
                    ? [...current, reason]
                    : current.filter((item) => item !== reason),
                });
              }}
            />
            {label}
          </label>
        ))}
      <label>
        Report received by
        <input readOnly value={portal.reportReceivedBy} />
      </label>
      <label>
        Updated owner email
        <input
          type="email"
          value={draft.updatedOwnerEmail ?? ''}
          onChange={(event) => set({ updatedOwnerEmail: event.target.value })}
        />
      </label>
      <div>
        <h4>Consent form</h4>
        <ReportPhotoEditor
          jobId={jobId}
          promptKey="consent-signed-form"
          photos={consent}
          accept="image/*,application/pdf"
          uploadLabel="Add consent form"
          onOpenPhoto={(_photo, url) => window.open(url, '_blank', 'noopener')}
          onUploadPhoto={busy ? undefined : onUpload}
          onDeletePhoto={busy ? undefined : onDelete}
        />
      </div>
    </div>
  );
}

function coverGaps(cover: JobCover): string[] {
  const missing: string[] = [];
  if (!cover.inspectionStartedAt?.trim()) missing.push('inspection start');
  if (!cover.inspectionEndedAt?.trim()) missing.push('inspection end');
  if (!cover.inspectorIdCardNumber?.trim()) missing.push('inspector ID card number');
  if (cover.roofCavityInspected === undefined) missing.push('roof cavity inspected');
  return missing;
}

function stamp(value: string | undefined): string {
  const text = value?.trim();
  if (!text) return '—';
  return text.slice(0, 16).replace('T', ' ');
}

function forInput(value: string | undefined): string {
  const text = value?.trim() ?? '';
  const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/.exec(text);
  return match?.[1] ?? '';
}

function shown(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return value.trim() || 'blank';
  if (typeof value === 'number') return String(value);
  if (value == null) return 'blank';
  return JSON.stringify(value);
}
